import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Spinner } from '@forwardflow/ui/Spinner'
import LocalStorage from '@forwardflow/services/LocalStorage'
import AuthService from '@forwardflow/services/AuthService'
import Icon from '@forwardflow/ui/Icons'
import { ENV } from '@forwardflow/helpers/configs'
import { DemoSignupForm } from '@forwardflow/forms/DemoSignupForm'
import { useUserNavigation } from '@forwardflow/hooks/customHooks/useUserNavigation'

const DemoSignupComponent = () => {
   const [searchParams] = useSearchParams()
   const [socialLogin, setSocialLogin] = useState(false)
   const [isSubmitted, setIsSubmitted] = useState(false)
   const [emailFromUrl, setEmailFromUrl] = useState<string | null>(null)
   const [nameFromUrl, setNameFromUrl] = useState<string | null>(null)
   const [showSignupForm, setShowSignupForm] = useState(false)
   const [verificationResult, setVerificationResult] = useState<{
      verified: boolean
   }>({
      verified: false,
   })
   const { navigateUser, getCurrentOrgId } = useUserNavigation()
   const demoBookingId = searchParams.get('demoBookingId')

   const handleSocialLogin = async () => {
      let data: { accessToken: string } | null = null
      const authPlatform = searchParams.get('auth')
      // get temporary token from search params
      const tempToken = searchParams.get('token')
      if (tempToken === null) return
      try {
         setSocialLogin(true)
         if (authPlatform === 'google' || authPlatform === 'outlook') {
            data = await AuthService.apiGenerateAccessToken(tempToken)
         } else {
            data = await AuthService.apiValidateEmail(tempToken)
         }
         if (data && data.accessToken) {
            LocalStorage.put('access_token', data.accessToken)
            const currentOrgId = await getCurrentOrgId()
            navigateUser({
               currentOrgId: currentOrgId || '',
               searchParams: location.search,
            })
         }
      } catch (error) {
         console.error('error', error)
      }
   }
   const handleSubmit = (submitted: boolean) => {
      if (submitted) {
         setIsSubmitted(true)
      } else {
         setIsSubmitted(false)
      }
   }

   const handleShowSignupForm = () => {
      setShowSignupForm(true)
   }

   useEffect(() => {
      const email = searchParams.get('email')
      const name = searchParams.get('name')
      if (email) {
         setEmailFromUrl(email)
      }
      if (name) {
         setNameFromUrl(name)
      }

      async function isGoogleMailVerified(domain: string) {
         const apiUrl = `https://dns.google.com/resolve?name=${domain}&type=MX`

         try {
            const response = await fetch(apiUrl)
            const data = await response.json()

            if (!data.Answer) {
               return { verified: false }
            }

            let hasVerificationRecord = false

            for (const record of data.Answer) {
               if (record.data.includes('google')) {
                  hasVerificationRecord = true
               }
            }

            if (hasVerificationRecord) {
               return {
                  verified: true,
               }
            }
         } catch (error) {
            return { verified: false }
         }
      }

      if (emailFromUrl) {
         isGoogleMailVerified(emailFromUrl?.split('@')[1])?.then((result) => {
            console.log(result)
            setVerificationResult(result as any)
         })
      }

      handleSocialLogin()
   }, [searchParams, emailFromUrl])

   return (
      <>
         <div
            className={`flex min-h-screen px-4 lg:px-[30px] flex-col  items-center text-center pt-14 md:pt-28 `}
         >
            <a href="/">
               <img
                  width={'165px'}
                  src="/images/fff-logo.png"
                  className="md:absolute left-10 top-10"
                  alt="ff icon"
               />
            </a>
            <Link
               className="bg-white/10 transition-all hover:bg-white/20 w-[200px] h-12 flexc rounded-full md:absolute mt-7 md:mt-0 top-10 right-4 md:right-16"
               to="/signin"
            >
               Sign in
            </Link>
            <h1 className="text-[20px] md:text-[24px] mt-[30px] mb-7 font-medium">
               Your Demo is scheduled
            </h1>
            <h2 className="text-[12px] md:text-[14px] mb-[54px] text-[#C9C9C9] max-w-[478px]">
               Your demo is scheduled! For the best experience, we recommend
               signing in with the Google account linked to your calendar to
               maximize automation & scheduling efficiency.
            </h2>

            {!socialLogin ? (
               !isSubmitted ? (
                  <>
                     <div className="w-full flex flex-col max-w-[450px] items-start mb-10">
                        {(!verificationResult?.verified || showSignupForm) && (
                           <>
                              <DemoSignupForm
                                 onSubmit={handleSubmit}
                                 initialEmail={emailFromUrl as string}
                                 firstName={nameFromUrl?.split(' ')[0] || ''}
                                 lastName={
                                    nameFromUrl
                                       ?.split(' ')
                                       .slice(1)
                                       .join(' ') || ''
                                 }
                              />
                              <div className="flex items-center w-full gap-x-3">
                                 <span className="h-[2px] bg-white/30 w-full flex flex-1"></span>
                                 <span className="text-[12px]">OR</span>
                                 <span className="h-[2px] bg-white/30 w-full flex flex-1"></span>
                              </div>
                           </>
                        )}
                     </div>
                     <a
                        className="rounded-xl group text-[14px] md:text-[18px] bg-black/40 h-14 md:h-[70px] max-w-[450px] w-full flexc relative border border-white/10 hover:border-white hover:bg-primary transition"
                        href={`${ENV.PUBLIC_FF_URL}auth/google?redirectUrl=${window.location.origin}&demoBookingId=${demoBookingId}`}
                     >
                        <Icon
                           name="google-icon"
                           className="absolute left-8 group-hover:brightness-0 group-hover:invert-[1] w-5 md:w-6"
                        />
                        <span>Continue with Google</span>
                     </a>
                     {!(!verificationResult?.verified || showSignupForm) && (
                        <button
                           className="mt-2 rounded-xl group text-[14px] md:text-[18px] bg-black/40 h-14 md:h-[70px] max-w-[450px] w-full flexc relative border border-white/10 hover:border-white hover:bg-primary transition"
                           onClick={handleShowSignupForm}
                        >
                           Sign up to Forward Flow
                        </button>
                     )}
                  </>
               ) : (
                  <>
                     <div className="w-full max-w-[450px] mx-auto bg-black/40 border border-white/20 rounded-xl p-8 flex flex-col justify-center">
                        <Icon name="Mail" className="mx-auto mb-5" />
                        <h2 className="text-[20px] font-semibold mb-10">
                           Check your email
                        </h2>
                        <div className="text-[14px] leading-8">
                           <p className="text-white/70">
                              We emailed a magic link to
                           </p>
                           <p>{emailFromUrl}</p>
                           <p className="mb-4 text-white/70">
                              Click the link to finish sign up
                           </p>
                        </div>
                     </div>
                  </>
               )
            ) : (
               <Spinner />
            )}
            <div className="my-7 flex justify-between max-w-[450px] w-full">
               <a
                  href="/terms"
                  className="text-[#CDCDCD] text-[12px] font-medium hover:text-[#CDCDCD]/80"
               >
                  Terms and Conditions
               </a>
               <a
                  href="/privacy"
                  className="text-[#CDCDCD] text-[12px] font-medium hover:text-[#CDCDCD]/80"
               >
                  Privacy Policy
               </a>
            </div>
         </div>
      </>
   )
}

export default DemoSignupComponent
